import { DestructableObject } from "../../../../destructables";

export class TaskManager extends DestructableObject {
	private tasks = new Set<() => any>();
	private timeout: number;
	private timeout_ms: number;
	private executing = false;
	private callback?: () => void;
	private promise?: Promise<void>;

	constructor(timeout_ms = 0) {
		super();
		this.timeout_ms = timeout_ms;
	}
	queue(task: () => any): void {
		if (this.destroyed) return;
		this.tasks.add(task);
		if (!this.executing) {
			this.executing = true;
			this.promise = new Promise<void>((res) => {
				this.callback = res;
			});
			clearTimeout(this.timeout);
			this.timeout = setTimeout(() => this.execute(), this.timeout_ms);
		}
	}
	private execute(): void {
		for (const task of this.tasks) {
			task();
			this.tasks.delete(task);
		}
		this.callback?.();
		this.executing = false;
		this.promise = undefined;
		this.callback = undefined;
	}
	async finished(): Promise<void> {
		return this.promise ?? Promise.resolve();
	}
	destroy(): void {
		super.destroy();
		this.tasks.clear();
		this.callback?.();
		this.executing = false;
		this.promise = undefined;
		this.callback = undefined;
	}
}

export class AsyncTaskManager {
	private tasks = new Set<() => any>();
	private timeout: number;
	private timeout_ms: number;
	private executing = false;
	private callback?: () => void;
	private promise?: Promise<void>;

	constructor(timeout_ms = 0) {
		this.timeout_ms = timeout_ms;
	}
	queue(task: () => any): void {
		this.tasks.add(task);
		if (!this.executing) {
			this.executing = true;
			this.promise = new Promise<void>((res) => {
				this.callback = res;
			});
			clearTimeout(this.timeout);
			this.timeout = setTimeout(() => this.execute(), this.timeout_ms);
		}
	}
	private async execute(): Promise<void> {
		for (const task of this.tasks) {
			await task();
			this.tasks.delete(task);
		}
		this.callback?.();
		this.executing = false;
		this.promise = undefined;
		this.callback = undefined;
	}
	async finished(): Promise<void> {
		return this.promise ?? Promise.resolve();
	}
}

export class SingleTaskManager<R> {
	private task: () => R;
	private timeout: number;
	private timeout_ms: number;
	private queued = false;
	private promise?: Promise<R>;
	private resolve?: (res: R) => void;
	private reject?: (e: Error) => void;

	constructor(task: () => R, timeout_ms = 0) {
		this.task = task;
		this.timeout_ms = timeout_ms;
	}
	queue(): void {
		if (!this.queued) {
			this.queued = true;
			if (this.promise === undefined) {
				// Only reinitialize promise if it does not already exist
				this.promise = new Promise<R>((resolve, reject) => {
					this.resolve = resolve;
					this.reject = reject;
				});
			}
			clearTimeout(this.timeout);
			this.timeout = setTimeout(() => this.execute(), this.timeout_ms);
		}
	}
	private async execute(): Promise<void> {
		this.queued = false;
		try {
			const result = await this.task();
			if (!this.queued) this.resolve?.(result);
		} catch (e) {
			this.reject?.(e);
		} finally {
			// If the task is queued again while executing, let it run again.
			if (!this.queued) {
				this.promise = undefined;
				this.resolve = undefined;
				this.reject = undefined;
			}
		}
	}
	cancel(): void {
		this.queued = false;
		this.promise = undefined;
		this.resolve = undefined;
	}
	async finished(): Promise<R | void> {
		return this.promise ?? Promise.resolve();
	}
}
